import { useCallback, useEffect, useState } from 'react';
import { isNative } from '../api/origin.ts';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'install-banner-dismissed';

/**
 * The install banner appears only when the browser actually offers an install
 * (Chrome fires `beforeinstallprompt`), and is gone for good once installed.
 * Dismissing hides it for the session, so it can come back on the next visit.
 */
export function usePwaInstall(): { canInstall: boolean; install: () => void; dismiss: () => void } {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1',
  );

  useEffect(() => {
    const capture = (event: Event) => {
      // Suppress the browser's own mini-infobar; the banner in the design is
      // what offers the install, and it needs the event to do it.
      event.preventDefault();
      setPrompt(event as BeforeInstallPromptEvent);
    };
    const installed = () => setPrompt(null);

    window.addEventListener('beforeinstallprompt', capture);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', capture);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const install = useCallback(() => {
    if (!prompt) return;
    void prompt.prompt();
    void prompt.userChoice.finally(() => setPrompt(null));
  }, [prompt]);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }, []);

  // `beforeinstallprompt` cannot fire inside the shell, so the guard is only
  // belt-and-braces — but an app offering to install itself is the sort of
  // thing that has to be impossible rather than unlikely.
  return { canInstall: !isNative && prompt !== null && !dismissed, install, dismiss };
}
